import React, { useState } from 'react';
import { Category } from '../types';
import { useTheme } from '../contexts/ThemeContext';

interface CategoryListProps {
  categories: Category[];
  loading: boolean;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
}

export function CategoryList({ categories, loading, onEdit, onDelete }: CategoryListProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Edit button: cores diferentes para dark/light para garantir contraste
  const editBg = isDark ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)';
  const editBgHover = isDark ? 'rgba(105, 110, 119, 0.2)' : 'rgba(139, 141, 152, 0.2)';
  const editIconColor = isDark ? '#EDEEF0' : '#1C2024'; // Branco no dark, escuro no light
  // Delete button: mesma cor em ambos os temas (rosa claro funciona bem)
  const deleteBg = 'rgba(229, 70, 102, 0.15)';
  const deleteBgHover = 'rgba(229, 70, 102, 0.2)';
  const deleteIconColor = '#FF949D'; // Rosa claro funciona bem em ambos
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    if (deleteConfirm === id) {
      onDelete(id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Carregando categorias">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Carregando...</span>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
        <p>Nenhuma categoria encontrada.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border" style={{ 
        backgroundColor: theme === 'dark' ? 'rgba(30, 30, 31, 0.5)' : '#ffffff',
        borderColor: 'var(--border-color)'
      }} role="table">
        <thead style={{ backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.2)' : '#f9fafb' }}>
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
              ID
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
              Cor
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
              Nome
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
              Descrição
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
              Atualizado em
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="divide-y" style={{ 
          backgroundColor: theme === 'dark' ? 'transparent' : '#ffffff',
          borderColor: theme === 'dark' ? '#2A2A2A' : 'var(--border-color)'
        }}>
          {categories.map((category) => (
            <tr key={category.id} className="transition-colors" style={{ 
              backgroundColor: theme === 'dark' ? 'transparent' : 'transparent'
            }}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                {category.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div
                  className="w-6 h-6 rounded-full border"
                  style={{ 
                    backgroundColor: category.cor,
                    borderColor: 'var(--border-color)'
                  }}
                  aria-label={`Cor da categoria: ${category.cor}`}
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {category.nome}
              </td>
              <td className="px-6 py-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                {category.descricao || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                {formatDate(category.atualizadoEm)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex justify-end gap-1">
                  {/* Edit icon button */}
                  <button
                    onClick={() => onEdit(category)}
                    className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                    title="Editar"
                    aria-label={`Editar categoria ${category.nome}`}
                    style={{
                      background: editBg,
                      color: editIconColor
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = editBgHover;
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = editBg;
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>
                  {/* Delete icon button */}
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                    title={deleteConfirm === category.id ? 'Confirmar exclusão' : 'Excluir'}
                    aria-label={
                      deleteConfirm === category.id
                        ? `Confirmar exclusão de ${category.nome}`
                        : `Excluir categoria ${category.nome}`
                    }
                    style={{
                      background: deleteConfirm === category.id ? 'rgba(229, 70, 102, 0.3)' : deleteBg,
                      color: deleteIconColor
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = deleteConfirm === category.id ? 'rgba(229, 70, 102, 0.4)' : deleteBgHover;
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = deleteConfirm === category.id ? 'rgba(229, 70, 102, 0.3)' : deleteBg;
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path d="M3 6h18" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}